import React from "react";
import axios from "http/customAxios";
import { defineMessages, FormattedMessage } from "react-intl";
import EntityForm from "../../EntityForm/EntityForm";
import { BASE_URL } from "../../../request";
import { INVOICE_TYPE } from "../../../Common/constants";
import { FullWidthLoader } from "../../../../../components/full-width-loader";
import { ContainerLoader } from "../../../CRUD/Promotions/PromotionsForm";
import { regenPropertyCache } from "../../../../common/api";
import { useHttpClient } from "../../../../../lib/http";
import { IConfig, useConfig } from "../../../../../lib/config";

const messages = defineMessages({
  conflictError: {
    id: "crm.contracts.conflictError",
    defaultMessage:
      "There is another active contract for that property with that service and criteria",
  },
  shouldHasPartner: {
    id: "crm.contracts.shouldHasPartner",
    defaultMessage: "Should has a partner associate.",
  },
  unitsGreater: {
    id: "crm.contracts.unitsGreater",
    defaultMessage: "Fixed charge contracts cannot be saved with units at 0.",
  },
  netGreater: {
    id: "crm.contracts.netGreater",
    defaultMessage: "$Net cannot be 0 or less.",
  },
  shouldHasAssociatedProperty: {
    id: "crm.contracts.shouldHasAssociatedProperty",
    defaultMessage: "Must have an associated property.",
  },
});

function customEntityToFormValuesMapper({ id, clients, entity }) {
  if (!entity || !id) {
    return {
      status: "live",
      propertyId: 1,
      property: "",
      serviceId: 1,
      service: "WitBooker",
      ruleId: 1,
      rule: "x mes",
      payable: "mes vencido",
      value: 0,
      minValue: 0,
      net: 1,
      discount: 0,
      currencyContract: "EUR",
      currencyInvoice: "EUR",
      percentagePartner: 0,
      invoicePartner: INVOICE_TYPE.DIRECT,
      setup: 0,
      deposit: 0,
    };
  }

  const actualEntity = { ...entity };
  actualEntity.invoicePartner = actualEntity.invoicePartner
    ? INVOICE_TYPE.PARTNER
    : INVOICE_TYPE.DIRECT;
  const invoice = clients.find(c => actualEntity && c.id === parseInt(actualEntity.invoice));
  actualEntity.property = {
    value: actualEntity.property,
    label: actualEntity.property,
  };
  actualEntity.invoice = {
    value: (invoice && invoice.id) || "",
    label: (invoice && invoice.name) || "",
  };
  actualEntity.discount = entity.discount * 100;
  actualEntity.percentagePartner = entity.percentagePartner * 100;
  return { ...actualEntity };
}

function getFields(fields, dictionaries, clients) {
  const clientsMap = clients.map(client => ({
    value: client.id,
    label: `${client.name}-#${client.id}`,
  }));
  if (dictionaries) {
    const servicesIndex = fields.form.findIndex(field => field.fieldName === "serviceId");
    Object.keys(dictionaries.services).forEach(service => {
      fields.form[servicesIndex].optionDisplay[dictionaries.services[service]] = { label: service };
    });
    const rulesIndex = fields.form.findIndex(field => field.fieldName === "ruleId");
    Object.keys(dictionaries.rules).forEach(rule => {
      fields.form[rulesIndex].optionDisplay[dictionaries.rules[rule]] = { label: rule };
    });
    const propertiesIndex = fields.form.findIndex(field => field.fieldName === "property");
    Object.keys(dictionaries.properties).forEach(property => {
      fields.form[propertiesIndex].options[dictionaries.properties[property]] = property;
    });
    if (fields.form[propertiesIndex]) {
      fields.form[propertiesIndex].options.sort();
    }
  }
  return (
    fields &&
    fields.form &&
    fields.form
      .sort((a, b) => (a.order > b.order ? 1 : -1))
      .map(it =>
        it.fieldName === "invoice"
          ? {
              ...it,
              options: clientsMap,
            }
          : it
      )
  );
}

class ContractsForm extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      clients: undefined,
      generalError: undefined,
    };
  }

  componentDidMount() {
    // TODO: replace with httpclient
    axios.get(`${BASE_URL}/api/contracts/dictionaries`).then(response => {
      if (response.status === 200 && response.data) {
        this.setState({ dictionaries: response.data });
      }
    });
    // TODO: replace with httpclient
    axios.get(`${BASE_URL}/api/clients?size=10000`).then(response => {
      if (response.status === 200 && response.data) {
        this.setState({ clients: response.data.content || [] });
      } else {
        this.setState({ clients: [] });
      }
    });
  }

  handleSubmit = ({ formValues }) => {
    const propertyValue = (formValues.property && formValues.property.value) || "";
    formValues.propertyId =
      this.state.dictionaries && this.state.dictionaries.properties[propertyValue];

    if (formValues.start && !formValues.propertyId) {
      this.setState({
        generalError: {
          localMessage: <FormattedMessage {...messages.shouldHaveAssociatedProperty} />,
        },
      });
      return;
    }

    if (formValues.rule === "x mes" && Number(formValues.units) === 0) {
      this.setState({
        generalError: { localMessage: <FormattedMessage {...messages.unitsGreater} /> },
      });
      return;
    }

    if (formValues.net <= 0) {
      this.setState({
        generalError: { localMessage: <FormattedMessage {...messages.netGreater} /> },
      });
      return;
    }

    delete formValues.translations;
    delete formValues.propertyTicker;

    const invoiceValue = (formValues.invoice && parseInt(formValues.invoice.value)) || "";

    formValues.service =
      this.state.dictionaries &&
      Object.keys(this.state.dictionaries.services).find(
        service => this.state.dictionaries.services[service] === parseInt(formValues.serviceId)
      );
    formValues.rule =
      this.state.dictionaries &&
      Object.keys(this.state.dictionaries.rules).find(
        rule => this.state.dictionaries.rules[rule] === parseInt(formValues.ruleId)
      );
    formValues.created = new Date();
    formValues.invoicePartner = formValues.invoicePartner === INVOICE_TYPE.PARTNER;
    if (
      (formValues.end && !formValues.start) ||
      (formValues.start && formValues.end && formValues.start > formValues.end)
    ) {
      this.setState({ generalError: { localMessage: <div>Rango de fechas inválido</div> } });
      return;
    } else {
      this.setState({ generalError: undefined });
    }
    formValues.discount /= 100;
    formValues.percentagePartner /= 100;
    this.props.onSubmitEntity({
      id: formValues.id,
      entity: { ...formValues, property: propertyValue, invoice: invoiceValue }, // this way we do not lose the form values when something goes wrong
      // requestQueueAfterMain: () =>
      //   regenPropertyCache(this.props.apiClient, propertyValue).then(() => ({})),
    });
  };

  render() {
    const { name, validation, fields, renderers, translation, locale } = this.props.config;
    const {
      id,
      locales,
      entities,
      entity,
      entityError,
      entityResult,
      entityName,
      loading,
      getListPath,
      relatedEntities,
      updateEntity,
    } = this.props;
    const { clients, dictionaries, generalError } = this.state;
    const { handleSubmit } = this;

    if (!clients) {
      return (
        <ContainerLoader>
          <FullWidthLoader />
        </ContainerLoader>
      );
    }

    if (entityError && entityError.code === "CONFLICT") {
      if (entityError?.message?.message?.includes("ERRORCODE: 416")) {
        entityError.localMessage = <FormattedMessage {...messages.shouldHasPartner} />;
      } else {
        entityError.localMessage = <FormattedMessage {...messages.conflictError} />;
      }
    }

    return (
      <EntityForm
        loading={loading}
        name={name}
        entity={entity}
        entities={entities}
        validation={validation}
        renderers={renderers}
        fields={getFields(fields, dictionaries, clients)}
        locales={locales}
        translation={translation}
        onSubmit={handleSubmit}
        entityError={generalError ? generalError : entityError}
        entityResult={entityResult}
        entityName={entityName}
        getListPath={getListPath}
        entityToFormValuesMapper={props =>
          customEntityToFormValuesMapper({
            id: parseInt(id),
            clients,
            ...props,
          })
        }
        relatedEntities={relatedEntities}
        locale={locale}
        updateEntity={updateEntity}
      />
    );
  }
}

ContractsForm.propTypes = {};

ContractsForm.defaultProps = {
  locales: ["es", "ca", "en", "fr", "it"],
  getListPath: function getListPath({ hotelTicker, entityName }) {
    return `/${hotelTicker}/${entityName}/`;
  },
};

function ContractFormWrapper(props) {
  const configuration = useConfig();
  const apiClient = useHttpClient(configuration.secureApiEndpoint);
  return <ContractsForm {...props} apiClient={apiClient} />;
}
export default ContractFormWrapper;
